/**
 * The one sentence and one colour at the top of Stages and queues: is anything wrong right now, the worst thing
 * first. Red means a person at Ademero must act now and nothing else: a customer's bad file, a backlog staff
 * acknowledged and work held by design are never red (Dustin, 2026-10-08: "4 parked, ours to fix" in red were a
 * test company's deliberate bad files, and nobody could tell what it meant). Pure.
 */
import type { Formatter } from '@paige/format';
import { partName } from '../pools/pools-words';
import type { FlowStatus } from './flow-state';
import type { AdminFlow, FlowIncident, FlowPart, FlowPlace } from './flow-types';
import { incidentHeadline, incidentSize } from './incident-words';
import { placeName } from './flow-words';

export type VerdictTone = 'red' | 'amber' | 'clear';

export interface Verdict {
  tone: VerdictTone;
  /** The sentence. */
  headline: string;
  /** What else is true, in a line: what Paige is doing, what is held by design. */
  detail: string;
  /** The incident or place the sentence is about, for its link; null when all is clear. */
  incident: FlowIncident | null;
  place: FlowPlace | null;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** A headline inside a sentence: lower case, except the names Paige and Cortexa. */
const inSentence = (words: string) =>
  /^(Paige|Cortexa)\b/.test(words) ? words : words.charAt(0).toLowerCase() + words.slice(1);

/** A pool or service down or short that starves a place with work. */
export function starvedBy(flow: AdminFlow): FlowPart | null {
  if (flow.partsStale) return null;
  const busy = new Set(
    flow.cells.filter((c) => c.waiting + c.working > 0).map((c) => c.place as string),
  );
  const hurt = flow.parts.filter(
    (p) => (p.state === 'down' || p.state === 'short') && p.starves.some((s) => busy.has(s)),
  );
  return hurt.find((p) => p.state === 'down') ?? hurt[0] ?? null;
}

/** Each place a pool or service that is down or short feeds, with that part (down before short). */
export function starvedPlaces(flow: AdminFlow): Map<FlowPlace, FlowPart> {
  const map = new Map<FlowPlace, FlowPart>();
  if (flow.partsStale) return map;
  const hurt = flow.parts
    .filter((p) => p.state === 'down' || p.state === 'short')
    .sort((a, b) => (a.state === 'down' ? 0 : 1) - (b.state === 'down' ? 0 : 1));
  for (const part of hurt)
    for (const place of part.starves)
      if (!map.has(place as FlowPlace)) map.set(place as FlowPlace, part);
  return map;
}

function partVerdict(flow: AdminFlow, part: FlowPart): Verdict {
  const place = part.starves.find((s) =>
    flow.cells.some((c) => c.place === s && c.waiting + c.working > 0),
  ) as FlowPlace;
  const down = part.state === 'down';
  return {
    tone: down ? 'red' : 'amber',
    headline: `${placeName[place]} is ${down ? 'stalled' : 'slow'}: ${partName(part.id)} has ${part.ready} of ${part.wanted} ready`,
    detail: down
      ? 'Work waits safely and moves once it is back. Engineering is alerted by the health monitor.'
      : 'Work keeps moving, more slowly, until the rest come back.',
    incident: null,
    place,
  };
}

function heldLine(f: Formatter, incidents: FlowIncident[]): string {
  const held = incidents.filter((i) => i.tone === 'neutral');
  if (held.length === 0) return 'Nothing is held.';
  const items = held.reduce((n, i) => n + i.items, 0);
  return `${f.int(items)} ${plural(items, 'item', 'items')} held by design or with the customer (grey): not a problem.`;
}

/** The sentence for the whole platform, from the picture and its incidents. */
export function verdictOf(f: Formatter, flow: AdminFlow, incidents: FlowIncident[]): Verdict {
  const part = starvedBy(flow);
  if (part?.state === 'down') return partVerdict(flow, part);
  const red = incidents.filter((i) => i.tone === 'red');
  if (red.length > 0) {
    const worst = red[0]!;
    const more = red.length > 1 ? ` and ${red.length - 1} more` : '';
    return {
      tone: 'red',
      headline: `${red.length} ${plural(red.length, 'thing needs', 'things need')} us: ${inSentence(incidentHeadline(worst))} (${incidentSize(f, worst)})${more}`,
      detail: heldLine(f, incidents),
      incident: worst,
      place: worst.origin,
    };
  }
  if (part) return partVerdict(flow, part);
  const amber = incidents.filter((i) => i.tone === 'amber');
  if (amber.length > 0) {
    const first = amber[0]!;
    const next =
      first.nextTrySeconds !== null ? `; next try in ${f.duration(first.nextTrySeconds)}` : '';
    const at = first.kind === 'slow' ? '' : ` at ${placeName[first.origin]}`;
    return {
      tone: 'amber',
      headline: `${incidentHeadline(first)}${at} (${incidentSize(f, first)})${next}`,
      detail: `Nothing needs us: Paige is handling it. ${heldLine(f, incidents)}`,
      incident: first,
      place: first.origin,
    };
  }
  return {
    tone: 'clear',
    headline: clearLine(f, flow),
    detail: heldLine(f, incidents),
    incident: null,
    place: null,
  };
}

/** All clear, with the day's pages when there are any: "0 pages today" first thing reads like nothing works. */
const clearLine = (f: Formatter, flow: AdminFlow): string =>
  flow.pagesToday > 0
    ? `All clear: ${f.int(flow.pagesToday)} pages today, nothing waiting on us`
    : 'All clear: nothing waiting on us, no pages yet today';

/** What "Hide test companies" did, said on the strip itself. */
export function testsWords(f: Formatter, flow: AdminFlow): string {
  if (!flow.testsHidden) return 'Test companies are counted.';
  const { companies, items } = flow.hidden;
  if (companies === 0) return 'Test companies hidden; none has work here.';
  return `Left out: ${f.int(items)} ${plural(items, 'item', 'items')} of ${f.int(companies)} test ${plural(companies, 'company', 'companies')}.`;
}

/** A parked note's colour from its incident's: red stays red, amber is slow, the rest are held. */
const NOTE_TONE = { red: 'stuck', amber: 'slow', neutral: 'held' } as const;

/**
 * A stage's colour once the incidents are known: red only where a red incident is, or a pool it needs is down; a
 * parked note that is the customer's, acknowledged or settling is held, and one Paige puts back by itself is slow.
 */
export function withIncidents(
  place: FlowPlace,
  status: FlowStatus,
  incidents: FlowIncident[],
  flow: AdminFlow,
): FlowStatus {
  const here = incidents.filter((i) => i.places.includes(place) && i.class === 'parked');
  const toneOf = new Map(here.map((i) => [i.reason, NOTE_TONE[i.tone]]));
  const notes = status.notes.map((n) =>
    n.kind === 'ours' ? { ...n, tone: toneOf.get(n.reason) ?? ('held' as const) } : n,
  );
  const starved =
    !flow.partsStale && flow.parts.some((p) => p.state === 'down' && p.starves.includes(place));
  if (notes.some((n) => n.tone === 'stuck') || (starved && status.here > 0))
    return { ...status, notes, tone: 'stuck' };
  if (status.tone !== 'stuck') return { ...status, notes };
  return { ...status, notes, tone: notes.some((n) => n.tone === 'slow') ? 'slow' : 'held' };
}
